import React from 'react';
import type { CurrentSubscription, Plan } from './types';
import {
  getFreeTierFromPlans,
  buildDowngradeUsageNote,
  formatPagesPerSiteLabel,
  isUnlimitedPages,
} from './planChange';

interface DowngradeModalProps {
  isOpen: boolean;
  plans: Plan[];
  currentSubscription: CurrentSubscription | null;
  downgrading: boolean;
  downgradeError: string | null;
  onConfirmDowngrade: () => void;
  onClose: () => void;
}

export const DowngradeModal: React.FC<DowngradeModalProps> = ({
  isOpen,
  plans,
  currentSubscription,
  downgrading,
  downgradeError,
  onConfirmDowngrade,
  onClose,
}) => {
  if (!isOpen) return null;

  const { maxSites, maxPagesPerSite, displayName, plan: freePlan } = getFreeTierFromPlans(plans);
  const pageText = isUnlimitedPages(maxPagesPerSite)
    ? 'unlimited pages and blog posts per site (combined)'
    : `up to ${formatPagesPerSiteLabel(maxPagesPerSite)} pages and blog posts per site (combined)`;
  const usageNote = buildDowngradeUsageNote(currentSubscription, maxSites);
  const currentName = currentSubscription?.plan?.name?.toUpperCase() ?? '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Downgrade to {displayName} plan?
        </h3>
        <p className="text-gray-600 mb-4">
          After your paid subscription ends, you will be on the{' '}
          <span className="font-semibold">{displayName}</span> plan, which includes up to{' '}
          <span className="font-semibold">{maxSites}</span> {maxSites === 1 ? 'site' : 'sites'} and{' '}
          {pageText}.
        </p>
        {usageNote && (
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mb-4">
            {usageNote}
          </p>
        )}
        {freePlan && (
          <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1 text-sm">
            {currentName === 'STARTER' && (
              <>
                <li>
                  Custom domain and contact forms (available on paid plans) will not be available
                  for new setup as on Free
                </li>
                <li>Feature limits will match the Free tier (see plan card for details)</li>
              </>
            )}
            {currentName === 'PROFESSIONAL' && (
              <>
                <li>
                  Advanced components and Compete Studio will no longer be available for new work
                </li>
                <li>
                  Stricter site and per-site page limits ({maxSites} site(s), {pageText})
                </li>
              </>
            )}
            {!['STARTER', 'PROFESSIONAL'].includes(currentName) && (
              <li>Your plan limits and features will match the {displayName} tier</li>
            )}
          </ul>
        )}
        {!downgradeError && (
          <p className="text-sm text-gray-500 mb-6">
            Your subscription will remain active until the end of your current billing period.
          </p>
        )}

        {downgradeError && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">{downgradeError}</p>
          </div>
        )}

        <div className="flex space-x-3">
          {!downgradeError ? (
            <>
              <button
                onClick={onConfirmDowngrade}
                disabled={downgrading}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downgrading ? 'Processing...' : 'Continue with Downgrade'}
              </button>
              <button
                onClick={onClose}
                disabled={downgrading}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
